import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../../../hooks/useAuth";
import Spinner from "../../../../shared/Spinner";
import { useCrumbs } from "../../../../hooks";
import {
  useResendCustomerInviteMutation,
} from "../../../../redux/services/customers/customersApi";
import { Badge, EditableText, Tabs, Card, Avatar, Button } from "../../../../shared";
import { isAuthorized } from "../../../../utils/authority";
import { SidebarContainer } from "../../../../components";
import CustomerBilling from "./Billing";
import { toast } from "react-toastify";
import Overview from "./Overview";
import { getCustomerOnboardingStatus } from "./onboardingStatus";
import SettingsTab from "./SettingsTab";
import ActivityTab from "./ActivityTab";
import DataIntegrationTab from "./DataIntegrationTab";
import EmailConfigModal from "./EmailConfigModal";
import ApiKeyModal from "./ApiKeyModal";
import CustomerTagsInline from "../CustomerTagsInline";
import {
  useGetAdminCustomerQuery,
  useUpdateAdminCustomerMutation,
} from "../../../../redux/services/admin/adminApi";

const UsersTab = ({ customer }) => {
  const contacts = Array.isArray(customer?.contacts) ? customer.contacts : [];
  const [resendCustomerInvite, { isLoading: isResendingInvite }] =
    useResendCustomerInviteMutation();

  async function handleResendInvite() {
    if (!customer?._id) {
      return;
    }

    try {
      const response = await resendCustomerInvite({ id: customer._id }).unwrap();
      toast(response?.message || "Onboarding email resent successfully", {
        type: "success",
      });
    } catch (error) {
      toast(error?.data?.message || "Unable to resend onboarding email", {
        type: "error",
      });
    }
  }

  return (
    <div className="space-y-6">
      <Card>
        <div className="flex flex-col gap-3 mb-4 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-medium">Customer Users</h3>
            <Badge
              color={contacts.length > 0 ? "green" : "gray"}
              label={`${contacts.length} ${contacts.length === 1 ? "user" : "users"}`}
            />
          </div>
          <Button
            type="alternative"
            onClick={handleResendInvite}
            disabled={!customer?._id || contacts.length === 0 || isResendingInvite}
          >
            {isResendingInvite ? "Sending..." : "Resend invite"}
          </Button>
        </div>

        {contacts.length === 0 ? (
          <p className="text-sm text-gray-500">
            No users are attached to this customer yet.
          </p>
        ) : (
          <div className="space-y-3">
            {contacts.map((contact) => (
              <div
                key={contact._id}
                className="flex items-center justify-between border rounded-lg px-4 py-3"
              >
                <div className="flex items-center gap-3">
                  <Avatar src={contact.avatar} name={contact.fullName} size="sm" />
                  <div>
                    <div className="font-medium text-gray-900">
                      {contact.fullName || contact.email}
                    </div>
                    <div className="text-sm text-gray-500">{contact.email}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge
                    color={contact.active ? "green" : "yellow"}
                    label={contact.active ? "Active" : "Pending"}
                  />
                  <Badge color="gray" label={contact.authority || "user"} />
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};

const CustomerShow = () => {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { setLoading: setCrumbsLoading, updateCrumbName } = useCrumbs();
  const [loading, setLoading] = useState(true);
  const [isEmailConfigModalOpen, setIsEmailConfigModalOpen] = useState(false);
  const [isApiKeyModalOpen, setIsApiKeyModalOpen] = useState(false);
  const [updateCustomer, { isLoading: isSavingTitle }] =
    useUpdateAdminCustomerMutation();

  const { data: customerData, isLoading: customerLoading, isError: customerError } =
    useGetAdminCustomerQuery(
      {
        id: params.id,
      },
      { skip: !params.id }
    );

  const customer = customerData?.customer || customerData?.data || null;
  const statusLabel = customer?.active ? "Active" : "Pending";
  const statusColor = customer?.active ? "green" : "yellow";
  const onboardingStatus =
    customer?.onboardingStatus || getCustomerOnboardingStatus(customer || {});

  useEffect(() => {
    setCrumbsLoading(true);
  }, []);

  useEffect(() => {
    if (customer?._id) {
      updateCrumbName({
        name: customer.name,
        path: "/admin/customers/" + customer._id,
      });
      setCrumbsLoading(false);
      setTimeout(() => setLoading(false), 500);
      return;
    }

    if (!customerLoading) {
      setCrumbsLoading(false);
      setLoading(false);
    }
  }, [customer?._id, customer?.name, customerLoading, updateCrumbName, setCrumbsLoading]);

  function handleGenerateApiKey() {
    setIsApiKeyModalOpen(true);
  }

  function handleEmailConfig() {
    setIsEmailConfigModalOpen(true);
  }

  async function handleTitleSave({ value }, setEditableLoading) {
    if (!customer?._id || !value || value === customer?.name) {
      return;
    }

    try {
      setEditableLoading(true);
      await updateCustomer({
        id: customer._id,
        data: {
          name: value,
        },
      }).unwrap();
      toast("Customer name updated", {
        type: "success",
        toastId: "customer-name-updated",
      });
    } catch (error) {
      toast(error?.data?.message || "Unable to update customer name", {
        type: "error",
        toastId: "customer-name-update-error",
      });
    } finally {
      setEditableLoading(false);
    }
  }

  async function handleTagsChange(tags) {
    if (!customer?._id) return;

    try {
      await updateCustomer({
        id: customer._id,
        data: {
          tags,
        },
      }).unwrap();
    } catch (error) {
      toast(error?.data?.message || "Unable to update customer tags", {
        type: "error",
        toastId: "customer-tags-profile-update-error",
      });
      throw error;
    }
  }

  function handleBackToCustomers() {
    navigate("/admin/customers");
  }

  function handleOpenBillingTab() {
    navigate(`/admin/customers/${customer?._id}?customer-show=Billing`);
  }

  function handleTabChange(item) {
    const tabId = item?.id;
    if (!tabId) return;

    const nextParams = new URLSearchParams(location.search);
    nextParams.set("customer-show", tabId);
    const nextSearch = nextParams.toString();

    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : "",
        hash: location.hash || "",
      },
      { replace: true }
    );
  }

  if (customerLoading && !customer?._id) {
    return (
      <div className="flex items-center justify-center h-full">
        <Spinner />
      </div>
    );
  }

  if (customerError || !customer?._id) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <p className="text-lg font-medium text-gray-900">
            Unable to load this customer.
          </p>
          <p className="mt-1 text-sm text-gray-500">
            The customer was not found or the id is invalid.
          </p>
          <Button
            className="mt-4"
            type="alternative"
            onClick={handleBackToCustomers}
          >
            All tenants
          </Button>
        </div>
      </div>
    );
  }

  const tabs = [
    {
      id: "Overview",
      title: "Overview",
      content: (
        <Overview
          customer={customer}
          onboardingStatus={onboardingStatus}
          onEmailConfig={handleEmailConfig}
          onApiKey={handleGenerateApiKey}
        />
      ),
    },
    {
      id: "Billing",
      title: "Billing",
      content: (
        <CustomerBilling
          user={user}
          customer={customer}
          loading={customerLoading || loading}
          setLoading={setLoading}
        />
      ),
    },
    {
      id: "Settings",
      title: "Settings",
      content: (
        <SettingsTab
          customer={customer}
          onboardingStatus={onboardingStatus}
        />
      ),
    },
    {
      id: "Users",
      title: "Users",
      content: <UsersTab customer={customer} />,
    },
    {
      id: "Data Integration",
      title: "Data Integration",
      content: <DataIntegrationTab customer={customer} />,
    },
    {
      id: "Activity",
      title: "Activity",
      content: (
        <ActivityTab
          customer={customer}
          onboardingStatus={onboardingStatus}
        />
      ),
    },
  ];
  const selectedTabId =
    new URLSearchParams(location.search).get("customer-show") || tabs[0]?.id;

  const title = (
    <div className="text-gray-800 font-semibold text-lg flex items-center">
      <EditableText
        className="ml-1"
        iconClasses="h-3 w-3"
        id="name"
        value={customer?.name}
        size="md"
        disabled={!isAuthorized("owner,admin")}
        displayText={(v) => (
          <span className="text-gray-800 font-semibold text-lg">{v}</span>
        )}
        onSave={handleTitleSave}
        loading={isSavingTitle}
      />
      <Badge color={statusColor} label={statusLabel} size="lg" className="ml-2" />
      <Badge
        color={onboardingStatus.color}
        label={onboardingStatus.label}
        size="lg"
        className="ml-2"
      />
      {customer?._id && (
        <CustomerTagsInline
          customer={customer}
          editable={isAuthorized("owner,admin")}
          onChangeTags={handleTagsChange}
          badgeSize="lg"
        />
      )}
    </div>
  );

  const actions = (
    <div className="flex items-center gap-2">
      <Button type="alternative" size="sm" onClick={handleBackToCustomers}>
        All tenants
      </Button>
      <Button
        size="sm"
        onClick={handleOpenBillingTab}
        disabled={!customer?._id}
      >
        Billing
      </Button>
    </div>
  );

  return (
    <div className="h-full min-h-0 flex flex-col">
      <SidebarContainer
        title={title}
        hideSidebar
        actions={actions}
        useViewportHeight={false}
        className="flex-1 min-h-0"
      >
        {({ wrapperStyle, panelStyle }) => (
          <Tabs
            className="w-full h-full flex flex-col"
            style={wrapperStyle}
            panelStyle={panelStyle}
            fullHeight
            panelClasses="border-t overflow-hidden"
            panelItemClasses="flex-1 min-h-0 overflow-y-auto p-2 pt-0"
            tabListClasses="bg-white px-2 h-[38px] flex-shrink-0"
            tabclassName="mx-1"
            id="customer-show"
            onChangeTab={handleTabChange}
            selectedId={selectedTabId}
            data={tabs}
          />
        )}
      </SidebarContainer>
      <EmailConfigModal
        customerId={customer?._id}
        visible={isEmailConfigModalOpen}
        setVisible={setIsEmailConfigModalOpen}
      />
      <ApiKeyModal
        customerId={customer?._id}
        visible={isApiKeyModalOpen}
        setVisible={setIsApiKeyModalOpen}
      />
    </div>
  );
};

export default CustomerShow;
